<template>
  <div>
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1>{{ $t('security_module') }}</h1>
          <p>Gestiona tu relación con las empresas de seguridad de forma centralizada.</p>
        </v-flex>
      </v-layout>

      <!-- SECCIÓN 1: CONTRATO ACTIVO O PENDIENTE - VISTA COMPLETA -->
      <v-layout row wrap v-if="hasAnyContract">
        <v-flex xs12>
          <h1 class="mb-4">{{ $t('security_active_contract') }}</h1>

          <v-card class="elevation-3 mb-4">
            <v-toolbar dark color="primary" class="mb-3">
              <v-toolbar-title>{{ activeContract.security_name }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-chip 
                :color="getContractStatusColor(activeContract.status)" 
                text-color="white"
              >
                {{ getContractStatusDisplay(activeContract.status) }}
              </v-chip>
            </v-toolbar>

            <v-card-text>
              <!-- Mensaje si contrato está pendiente de aprobación -->
              <v-alert 
                v-if="activeContract.status === 'PENDING'" 
                type="warning" 
                class="mb-4"
                icon="info"
              >
                {{ $t('security_contract_pending_approval') }} 
                El administrador de {{ activeContract.security_name }} debe aceptar este contrato para que se active.
              </v-alert>

              <!-- Información Principal -->
              <v-layout row wrap class="mb-4">
                <v-flex xs12 md6>
                  <h3 class="mb-3">{{ $t('security_contract_info') }}</h3>
                  <v-list dense>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ $t('security_company') }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ activeContract.security_name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ $t('Empresa') }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ activeContract.host_name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ $t('created_at') }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ formatDate(activeContract.created_at) }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile v-if="activeContract.pending_invitation">
                      <v-list-tile-content>
                        <v-list-tile-title>{{ $t('pending_invitation') }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ activeContract.pending_invitation }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-flex>

                <v-flex xs12 md6>
                  <h3 class="mb-3">{{ $t('security_next_steps') }}</h3>
                  <v-list dense>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>
                          <v-icon color="primary" small class="mr-2">check_circle</v-icon>
                          {{ $t('security_next_step_1') }}
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>
                          <v-icon color="primary" small class="mr-2">check_circle</v-icon>
                          {{ $t('security_next_step_2') }}
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>
                          <v-icon color="primary" small class="mr-2">check_circle</v-icon>
                          {{ $t('security_next_step_3') }}
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-flex>
              </v-layout>

              <!-- Botones de Acción -->
              <v-divider class="my-4"></v-divider>
              <v-layout row wrap>
                <v-flex xs12 sm6 class="pr-2">
                  <v-btn 
                    color="warning" 
                    block
                    @click="showChangeProviderDialog = true"
                  >
                    <v-icon left>swap_horiz</v-icon>
                    {{ $t('security_change_provider') }}
                  </v-btn>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                  <v-btn 
                    color="error" 
                    block
                    @click="showDeleteDialog = true"
                  >
                    <v-icon left>delete</v-icon>
                    {{ $t('security_unlink_company') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- SECCIÓN 1.5: INVITACIÓN PENDIENTE SIN CONTRATO -->
      <v-layout row wrap v-if="!hasAnyContract && hasPendingInvitation">
        <v-flex xs12>
          <v-card class="elevation-1 mb-4 pending-invitation-card">
            <v-card-text class="pa-4">
              <div class="pending-invitation-header mb-3">
                <v-icon color="grey darken-1" class="mr-2" style="vertical-align: middle;">hourglass_empty</v-icon>
                <span class="subheading grey--text text--darken-2 font-weight-medium">Invitación pendiente de respuesta</span>
              </div>
              <p class="body-1 grey--text text--darken-1 mb-4">
                Se envió una invitación a un administrador de seguridad. Deberás esperar a que sea aceptada antes de poder enviar una nueva.
              </p>
              <v-divider class="mb-3"></v-divider>
              <v-layout row wrap>
                <v-flex xs12 sm4 class="pending-detail-item">
                  <span class="caption grey--text text--darken-1">Invitado</span>
                  <div class="body-2 mt-1">
                    <v-icon small color="grey darken-1" class="mr-1">person_outline</v-icon>
                    {{ getPendingInvitation.first_name }}
                  </div>
                </v-flex>
                <v-flex xs12 sm4 class="pending-detail-item">
                  <span class="caption grey--text text--darken-1">Correo electrónico</span>
                  <div class="body-2 mt-1">
                    <v-icon small color="grey darken-1" class="mr-1">mail_outline</v-icon>
                    {{ getPendingInvitation.email }}
                  </div>
                </v-flex>
                <v-flex xs12 sm4 class="pending-detail-item">
                  <span class="caption grey--text text--darken-1">Enviada por</span>
                  <div class="body-2 mt-1">
                    <v-icon small color="grey darken-1" class="mr-1">send</v-icon>
                    {{ getPendingInvitation.invited_by }}
                  </div>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- SECCIÓN 2: SIN CONTRATO - VINCULAR O INVITAR -->
      <v-layout row wrap v-if="!hasAnyContract && !hasPendingInvitation">
        <v-flex xs12 md8>
          <v-layout row wrap>
            <v-flex xs12>
              <v-card class="elevation-3 mb-4">
                <v-toolbar dark color="primary" class="mb-3">
                  <v-toolbar-title>{{ $t('security_link_company') }}</v-toolbar-title>
                </v-toolbar>

                <v-card-text>
                  <p>{{ $t('security_link_company_description') }}</p>

                  <v-progress-linear v-if="isSecurityLoading" indeterminate class="mb-3"></v-progress-linear>

                  <v-alert v-if="getSecurityError" type="error" dismissible class="mb-3">
                    {{ getSecurityError }}
                  </v-alert>

                  <v-list v-if="availableCompanies.length > 0" two-line>
                    <v-list-tile
                      v-for="(company, index) in availableCompanies"
                      :key="company.id"
                    >
                      <v-list-tile-content>
                        <v-list-tile-title>{{ company.name || (company.companySecurityProvider ? company.companySecurityProvider.nombre : '') }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ company.email || (company.companySecurityProvider ? company.companySecurityProvider.email : '') }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                      <v-list-tile-action>
                        <v-btn
                          flat
                          color="primary"
                          @click="selectCompany(company)"
                          :loading="linkingCompanyId === company.id"
                        >
                          {{ $t('select') }}
                        </v-btn>
                      </v-list-tile-action>
                    </v-list-tile>
                  </v-list>
                  <v-alert v-else-if="!isSecurityLoading" type="info">
                    {{ $t('security_no_available_companies') }}
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-flex>

        <!-- INVITAR ADMIN -->
            <v-flex xs12>
              <v-card class="elevation-3 mt-4">
                <v-toolbar dark color="primary" class="mb-3">
                  <v-toolbar-title>{{ $t('security_invite_admin') }}</v-toolbar-title>
                </v-toolbar>

                <v-card-text>
                  <p>{{ $t('security_invite_admin_description') }}</p>

                  <v-alert v-if="hasAnyContract" type="error" dismissible class="mb-3">
                    <strong>Contrato Existente</strong><br>
                    Ya existe un contrato con estado: <strong>{{ activeContract.status_display || activeContract.status }}</strong>
                    <div class="mt-2">
                      <p class="mb-0 caption">Deberá desenlazar la empresa actual antes de poder invitar una nueva.</p>
                    </div>
                  </v-alert>

                  <v-alert v-if="getSecurityError" type="error" dismissible class="mb-3">
                    {{ getSecurityError }}
                  </v-alert>

                  <v-alert v-if="invitationSent" type="info" class="mb-3">
                    {{ $t('security_invitation_pending_message') }}
                  </v-alert>

                  <v-form @submit.prevent="submitInvitation" ref="invitationForm" v-if="!invitationSent && !hasAnyContract && !hasPendingInvitation">
                    <v-text-field
                      v-model="form.email"
                      :label="$t('email')"
                      type="email"
                      required
                      :error-messages="errors.email"
                      @blur="validateEmail"
                      class="mb-3"
                    ></v-text-field>

                    <v-text-field
                      v-model="form.firstName"
                      :label="$t('first_name')"
                      required
                      :error-messages="errors.firstName"
                      @blur="validateFirstName"
                      class="mb-3"
                    ></v-text-field>

                    <v-text-field
                      v-model="form.lastName"
                      :label="$t('last_name')"
                      required
                      :error-messages="errors.lastName"
                      @blur="validateLastName"
                      class="mb-3"
                    ></v-text-field>

                    <v-btn
                      color="primary"
                      type="submit"
                      :loading="isSecurityLoading"
                      :disabled="!formValid"
                    >
                      {{ $t('security_send_invitation') }}
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs12 md4>
          <v-card class="elevation-3">
            <v-card-title primary-title>
              <div>
                <h3 class="headline mb-0">{{ $t('security_creation_flow') }}</h3>
              </div>
            </v-card-title>
            <v-card-text>
              <v-timeline>
                <v-timeline-item small color="primary">
                  <v-card class="mb-3">
                    <v-card-text>
                      <p class="mb-0"><strong>{{ $t('step') }} 1:</strong></p>
                      <p small>{{ $t('security_flow_step1') }}</p>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>

                <v-timeline-item small color="primary">
                  <v-card class="mb-3">
                    <v-card-text>
                      <p class="mb-0"><strong>{{ $t('step') }} 2:</strong></p>
                      <p small>{{ $t('security_flow_step2') }}</p>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>

                <v-timeline-item small color="primary">
                  <v-card class="mb-3">
                    <v-card-text>
                      <p class="mb-0"><strong>{{ $t('step') }} 3:</strong></p>
                      <p small>{{ $t('security_flow_step3') }}</p>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
      <v-dialog v-model="showConfirmDialog" max-width="400px">
        <v-card>
          <v-card-title>{{ $t('security_confirm_link') }}</v-card-title>
          <v-card-text>
            <p v-if="selectedCompany">{{ $t('confirm_link_msg') }} {{ selectedCompany.name || (selectedCompany.companySecurityProvider ? selectedCompany.companySecurityProvider.nombre : '') }}?</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showConfirmDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn color="primary" flat @click="linkCompany" :loading="isSecurityLoading">
              {{ $t('confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirmation Dialog para desvincular -->
      <v-dialog v-model="showDeleteDialog" max-width="400px">
        <v-card>
          <v-card-title>{{ $t('security_confirm_unlink') }}</v-card-title>
          <v-card-text>
            <p>{{ $t('confirm_unlink_msg') }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showDeleteDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn color="error" flat @click="unlinkCompany" :loading="isSecurityLoading">
              {{ $t('unlink') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog para cambiar proveedor -->
      <v-dialog v-model="showChangeProviderDialog" max-width="500px">
        <v-card>
          <v-card-title>{{ $t('security_change_provider') }}</v-card-title>
          <v-card-text>
            <p class="mb-3">{{ $t('security_change_provider_description') }}</p>
            <p class="mb-4" v-if="changeProviderCompanies.length === 0">
              <strong>{{ $t('security_no_other_providers') }}</strong>
            </p>
            <v-list v-if="changeProviderCompanies.length > 0" two-line>
              <v-list-tile
                v-for="company in changeProviderCompanies"
                :key="company.id"
              >
                <v-list-tile-content>
                  <v-list-tile-title>{{ company.name || (company.companySecurityProvider ? company.companySecurityProvider.nombre : '') }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ company.email || (company.companySecurityProvider ? company.companySecurityProvider.email : '') }}</v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-btn
                    flat
                    color="primary"
                    @click="selectCompany(company)"
                    :loading="linkingCompanyId === company.id"
                  >
                    {{ $t('select') }}
                  </v-btn>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showChangeProviderDialog = false">
              {{ $t('cancel') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { FETCH_AVAILABLE_COMPANIES, LINK_COMPANY, DELETE_LINK, FETCH_CONTRACT, INVITE_SECURITY_ADMIN } from '../../../store/modules/security'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityDashboard',
  data() {
    return {
      showConfirmDialog: false,
      showDeleteDialog: false,
      showChangeProviderDialog: false,
      selectedCompany: null,
      linkingCompanyId: null,
      invitationSent: false,
      form: {
        email: '',
        firstName: '',
        lastName: ''
      },
      errors: {
        email: [],
        firstName: [],
        lastName: []
      },
      successMessage: '',
      emailRegex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    }
  },
  computed: {
    ...mapGetters(['getToken']),
    ...mapGetters('security', [
      'getAvailableCompanies',
      'isSecurityLoading',
      'getSecurityError',
      'getContract',
      'hasAnyContract',
      'hasPendingInvitation',
      'getPendingInvitation'
    ]),
    availableCompanies() {
      return this.getAvailableCompanies
    },
    activeContract() {
      return this.getContract
    },
    changeProviderCompanies() {
      if (!Array.isArray(this.availableCompanies)) return []
      if (!this.hasAnyContract || !this.activeContract) return this.availableCompanies

      const excludedCompanyId = this.extractContractCompanyId(this.activeContract)
      const excludedCompanyName = (this.activeContract.security_name || '').toString().trim().toLowerCase()

      return this.availableCompanies.filter(company => {
        const currentId = this.extractCompanyId(company)
        const currentName = (company.name || (company.companySecurityProvider ? company.companySecurityProvider.nombre : '') || '')
          .toString()
          .trim()
          .toLowerCase()

        const sameById = excludedCompanyId !== null && currentId === excludedCompanyId
        const sameByName = !!excludedCompanyName && currentName === excludedCompanyName

        return !(sameById || sameByName)
      })
    },
    formValid() {
      return (
        this.form.email &&
        this.emailRegex.test(this.form.email) &&
        this.form.firstName &&
        this.form.firstName.trim().length > 0 &&
        this.form.lastName &&
        this.form.lastName.trim().length > 0
      )
    }
  },
  methods: {
    extractCompanyId(company) {
      if (!company || typeof company !== 'object') return null

      const directId = company.id
      if (directId !== undefined && directId !== null) return String(directId)

      const nestedId = company.companySecurityProvider && company.companySecurityProvider.id
      if (nestedId !== undefined && nestedId !== null) return String(nestedId)

      return null
    },
    extractContractCompanyId(contract) {
      if (!contract || typeof contract !== 'object') return null

      const candidates = [
        contract.security_company_id,
        contract.security_company,
        contract.company_security_id,
        contract.provider_id,
        contract.security_provider_id,
        contract.id_security_company,
        contract.security && contract.security.id,
        contract.security_company_data && contract.security_company_data.id
      ]

      const found = candidates.find(value => value !== undefined && value !== null)
      return found !== undefined && found !== null ? String(found) : null
    },
    scrollToSection(refName) {
      this.$nextTick(() => {
        const element = this.$refs[refName]
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
    selectCompany(company) {
      this.selectedCompany = company
      this.showConfirmDialog = true
      this.showChangeProviderDialog = false
    },
    linkCompany() {
      this.linkingCompanyId = this.selectedCompany.id
      this.$store.dispatch(`security/${LINK_COMPANY}`, {
        token: this.getToken,
        securityCompanyId: this.selectedCompany.id
      })
      .then(() => {
        this.showConfirmDialog = false
        this.$store.commit('snackbar_change_message', this.$t('security_company_linked_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_linking_company'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.linkingCompanyId = null
      })
    },
    unlinkCompany() {
      this.$store.dispatch(`security/${DELETE_LINK}`, this.getToken)
      .then((response) => {
        this.showDeleteDialog = false
        const guardsUnassigned = response.guards_unassigned || 0
        const message = guardsUnassigned > 0 
          ? `Empresa de seguridad desvinculada. ${guardsUnassigned} guardia(s) desasignado(s).`
          : 'Empresa de seguridad desvinculada correctamente.'
        this.$store.commit('snackbar_change_message', message)
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_unlinking_company'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    validateEmail() {
      this.errors.email = []
      if (!this.form.email) {
        this.errors.email.push(this.$t('email_required'))
      } else if (!this.emailRegex.test(this.form.email)) {
        this.errors.email.push(this.$t('email_invalid'))
      }
    },
    validateFirstName() {
      this.errors.firstName = []
      if (!this.form.firstName || this.form.firstName.trim().length === 0) {
        this.errors.firstName.push(this.$t('first_name_required'))
      }
    },
    validateLastName() {
      this.errors.lastName = []
      if (!this.form.lastName || this.form.lastName.trim().length === 0) {
        this.errors.lastName.push(this.$t('last_name_required'))
      }
    },
    submitInvitation() {
      this.validateEmail()
      this.validateFirstName()
      this.validateLastName()

      if (this.formValid) {
        this.$store.dispatch(`security/${INVITE_SECURITY_ADMIN}`, {
          token: this.getToken,
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName
        })
        .then(response => {
          this.invitationSent = true
          this.$store.commit('snackbar_change_message', this.$t('security_invitation_sent_success'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.form = {
            email: '',
            firstName: '',
            lastName: ''
          }
          this.errors = {
            email: [],
            firstName: [],
            lastName: []
          }
          this.successMessage = ''
        })
        .catch(err => {
          this.$store.commit('snackbar_change_message', this.$t('error_sending_invitation'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
      }
    },
    fetchData() {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_AVAILABLE_COMPANIES}`, token)
      this.$store.dispatch(`security/${FETCH_CONTRACT}`, token)
    },
    getContractStatusColor(status) {
      const statusColors = {
        'ACTIVE': 'success',
        'PENDING': 'warning',
        'REJECTED': 'error',
        'INACTIVE': 'grey'
      }
      return statusColors[status] || 'grey'
    },
    getContractStatusDisplay(status) {
      const statusDisplay = {
        'ACTIVE': 'Activo',
        'PENDING': 'Pendiente de Aprobación',
        'REJECTED': 'Rechazado',
        'INACTIVE': 'Inactivo'
      }
      return statusDisplay[status] || status
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }
    this.fetchData()
  }
}
</script>

<style scoped>
.mb-4 {
  margin-bottom: 30px;
}
.mb-3 {
  margin-bottom: 20px;
}
.mt-4 {
  margin-top: 20px;
}
small {
  font-size: 0.85rem;
}
.pending-invitation-card {
  border-left: 4px solid #9e9e9e;
  background-color: #fafafa;
}
.pending-invitation-header {
  display: flex;
  align-items: center;
}
.pending-detail-item {
  padding: 4px 8px;
}
</style>
